// Gym module domain types — mirrors Section 3 of gym-module-spec.md.
// The api/ backend keeps a copy at api/src/shared/gym.types.ts; extract to a
// shared package once the monorepo lands.

export interface GymProfile {
  userId: string; // FK to platform user (Entra ID object id)
  fullName: string; // collected via form — Entra ID token only exposes work email
  mobileNumber: string; // used for SMS reminders / no-show warnings
  sex: "male" | "female";
  medicalConditions?: string;
  profileCompletedAt: string | null;
  noShowCount: number;
  isBlocked: boolean; // set true after N no-shows, threshold TBD
}

export interface GymSlot {
  id: string;
  date: string; // ISO date, e.g. "2026-08-20"
  startTime: string; // "08:00", "08:30" — 30-minute slots, 08:00–20:00
  endTime: string;
  capacity: number; // default 20
  bookedCount: number; // denormalized for fast reads; Redis + Booking table are source of truth
}

export type BookingStatus = "confirmed" | "cancelled" | "attended" | "no_show";
export type BookingType = "regular" | "private";

export interface Booking {
  id: string;
  slotId: string;
  userId: string;
  status: BookingStatus;
  bookedAt: string;
  cancelledAt?: string;
  type: BookingType;
  packageBookingId?: string; // set if type === "private"
}

/** A booking plus the attendee's identity — the coach roster shape. */
export interface BookingWithAttendee extends Booking {
  fullName: string;
  mobileNumber: string;
}

/**
 * Admin override for a specific date. No row = default rules: closed on the
 * weekend (GYM_RULES.weeklyClosedDays), open with GYM_RULES.slotCapacity on
 * every other day. An override's isClosed always wins over the weekly
 * default, so a specific Friday/Saturday can be opened back up.
 */
export interface DayOverride {
  date: string;
  isClosed: boolean;
  capacityOverride: number | null;
  note?: string;
  updatedAt: string;
  updatedByUserId: string;
}

/**
 * Admin override for one specific slot on one date. No row = the day's rules
 * (default weekend closure, or a DayOverride) apply to that slot unchanged.
 */
export interface SlotOverride {
  date: string;
  startTime: string;
  isClosed: boolean;
  capacityOverride: number | null;
  note?: string;
  updatedAt: string;
  updatedByUserId: string;
}

export interface AttendanceRecord {
  id: string;
  bookingId: string;
  verifiedBy: "coach" | "hikvision";
  verifiedByUserId?: string; // coach id, if manual
  checkedInAt: string;
}

export interface SessionPackage {
  id: string;
  userId: string;
  totalCredits: number;
  remainingCredits: number;
  purchasedAt: string;
  expiresAt?: string;
}

// The subset of GymProfile the user supplies via the form. userId comes from
// auth; noShowCount / isBlocked / profileCompletedAt are system-managed.
export interface ProfileInput {
  fullName: string;
  mobileNumber: string;
  sex: "male" | "female";
  medicalConditions?: string;
}

// ---- Business rules (Section 2) as constants, so UI and API agree ----

export const GYM_RULES = {
  slotCapacity: 20,
  opensAt: "08:00",
  closesAt: "20:00",
  slotMinutes: 45,
  maxBookingsPerUserPerDay: 1,
  bookingWindowDays: 14, // rolling, opens exactly 2 weeks ahead
  cancellationCutoffHours: 2,
  // Egypt's weekend. JS/UTC day-of-week: 0=Sun, 1=Mon, ..., 5=Fri, 6=Sat.
  weeklyClosedDays: [5, 6] as number[],
  // The 14:00-14:45 slot every day is reserved for female employees only.
  ladiesOnlyStartTime: "14:00" as string | null,
} as const;
